import type { ColumnDef } from './types';

export type ColumnAlign = NonNullable<ColumnDef<unknown>['align']>;

const DEFAULT_ALIGN: ColumnAlign = 'left';

// Plain header cells and body cells align their inline content via text-align.
const TEXT_ALIGN_CLASS: Record<ColumnAlign, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

// A sortable header's label sits in a flex <button>, which ignores text-align,
// so it has to be justified instead. Right-aligned columns reverse the row so
// the sort caret sits before the label and the label's right edge lines up with
// the right-aligned values beneath it (row-reverse flips main-start to the
// right, hence justify-start).
const SORT_BUTTON_ALIGN_CLASS: Record<ColumnAlign, string> = {
  left: 'justify-start',
  right: 'flex-row-reverse justify-start',
  center: 'justify-center',
};

// Loading-skeleton bars are block elements — push them to the column's side.
const SKELETON_ALIGN_CLASS: Record<ColumnAlign, string> = {
  left: '',
  right: 'ml-auto',
  center: 'mx-auto',
};

function columnAlign<T>(col: ColumnDef<T>): ColumnAlign {
  return col.align ?? DEFAULT_ALIGN;
}

/** text-align class for a column's header and body cells. */
export function textAlignClass<T>(col: ColumnDef<T>): string {
  return TEXT_ALIGN_CLASS[columnAlign(col)];
}

/** Justify class for a sortable header's flex sort button. */
export function sortButtonAlignClass<T>(col: ColumnDef<T>): string {
  return SORT_BUTTON_ALIGN_CLASS[columnAlign(col)];
}

/** Placement class for a column's loading-skeleton bar or pill. */
export function skeletonAlignClass<T>(col: ColumnDef<T>): string {
  return SKELETON_ALIGN_CLASS[columnAlign(col)];
}
